import * as React from 'react';
import { Form, Text, Select } from 'react-form';
import { BaseFormProps } from '../../interfaces';
import { QuestionText, Language, Characteristic, QuestionOption, QuestionOptionText } from '../../models';
import { CharacteristicDataService, LanguageDataService, NotificationService } from '../../services';

interface QuestionEditState {
    characteristics: Characteristic[];
    languages: Language[];
    addLanguageId: number;
}

interface QuestionFormProps extends BaseFormProps {
    notificationService: NotificationService;
    hasPublishedVersion: boolean;
}

export class QuestionForm extends React.Component<QuestionFormProps, QuestionEditState> {
    private characteristicDataService = new CharacteristicDataService(this.props.notificationService);
    private languageDataService = new LanguageDataService(this.props.notificationService);

    constructor(props: QuestionFormProps) {
        super(props);

        this.props.defaultValues.questionTexts = this.props.defaultValues.questionTexts || [];
        this.props.defaultValues.characteristicScores = this.props.defaultValues.characteristicScores || [];
        this.props.defaultValues.scoreWeights = this.props.defaultValues.scoreWeights || [];
        this.props.defaultValues.addLanguageId = 1;
        this.props.defaultValues.questionOptions = this.props.defaultValues.questionOptions || [];
    }

    public componentDidMount() {

        this.setState({
            characteristics: [],
            languages: [],
            addLanguageId: 1
        });

        this.characteristicDataService.getAll().then((characteristics: Characteristic[]) => {
            this.setState({
                characteristics: characteristics
            });

            this.languageDataService.getAll().then((languages: Language[]) => {
                this.setState({
                    languages: languages
                });
                this.forceUpdate();
            });
        });
    }

    private createQuestionOption(sort: number, text: string, value: number) {
        var opt = new QuestionOption();
        opt.sort = sort;
        opt.value = value;
        opt.questionOptionTexts = [];
        var optText = new QuestionOptionText();
        optText.languageId = 1;
        optText.value = text;
        opt.questionOptionTexts.push(optText);
        opt.scoreWeights = [];
        
        return opt;
    }

    private setupScale(formApi: any) {
        formApi.values.questionOptions = [];

        formApi.values.questionOptions.push(this.createQuestionOption(0, '1', 1));
        formApi.values.questionOptions.push(this.createQuestionOption(1, '2', 2));
        formApi.values.questionOptions.push(this.createQuestionOption(2, '3', 3));
        formApi.values.questionOptions.push(this.createQuestionOption(3, '4', 4));
        formApi.values.questionOptions.push(this.createQuestionOption(4, '5', 5));

        formApi.setValue('questionOptions', formApi.values.questionOptions);
    }

    private setupBinary(formApi: any) {
        formApi.values.questionOptions = [];

        formApi.values.questionOptions.push(this.createQuestionOption(0, 'True', 1));
        formApi.values.questionOptions.push(this.createQuestionOption(1, 'False', 2));

        formApi.setValue('questionOptions', formApi.values.questionOptions);

        this.forceUpdate();
    }

    private questionTypeChange(formApi: any, value: any) {
        if (value === 'scale') {
            this.setupScale(formApi);
        } else if (value === 'binary') {
            this.setupBinary(formApi);
            formApi.setValue('visualStyle', 'trueFalse');
        }
        this.forceUpdate();
    }

    public render() {

        if (!this.state) {
            return <div />;
        }

        const languages: any = [];
        if (this.state.languages) {
            this.state.languages.map((x: Language) => {
                languages.push({
                    label: x.name,
                    value: x.id
                });
            });
        }

        const questionTypes = [{
            label: 'Likert',
            value: 'scale'
        },
        {
            label: 'True/False',
            value: 'binary'
        },
        {
            label: 'Text',
            value: 'text'
        },
        {
            label: 'Free Text',
            value: 'freeText'
        }];

        const visualStyles = [{
            label: 'True False',
            value: 'trueFalse'
        },
        {
            label: 'Either/Or',
            value: 'eitherOr'
        }];

        return (
            <Form defaultValues={this.props.defaultValues} onSubmit={submittedValues => this.props.submit(submittedValues)}>
                {formApi => (
                    <form onSubmit={formApi.submitForm} id="entityForm">
                        <div className="form-group">
                            <label htmlFor="name">Statement Type</label>
                            <Select disabled={this.props.hasPublishedVersion} className="form-control" field="questionType" options={questionTypes} onChange={(e) => this.questionTypeChange(formApi, e)} />
                        </div>
                        {
                            formApi.values.questionType === 'binary' && 
                            <div className="form-group">
                                <label htmlFor="name">Visual Style</label>
                                <Select className="form-control" field="visualStyle" options={visualStyles} />
                            </div>
                        }

                        <h3>Statement Text</h3>
                        {
                            formApi.values.questionTexts.map((text: QuestionText, ind: number) => {
                                return <div key={`questionText-${ind}`}>
                                    <div className="row">
                                        <div className="col-xs-7">
                                            <div className="form-group">
                                                <label htmlFor="name">Statement Text</label>
                                                <Text className="form-control" field={['questionTexts', ind, 'value']} id={'questionTexts' + ind} />
                                            </div>
                                        </div>
                                        <div className="col-xs-4">
                                            <div className="form-group">
                                                <label htmlFor="name">Language</label>
                                                <Select className="form-control" field={['questionTexts', ind, 'languageId']} id={'questionTextsLang' + ind} options={languages} />
                                            </div>
                                        </div>
                                        <div className="col-xs-1">
                                            <label htmlFor="name">&nbsp;</label>
                                            <a className="btn btn-danger" onClick={(e) => this.removeLanguage(formApi.values, ind)}>Delete</a>
                                        </div>
                                    </div>
                                </div>;
                            })
                        }

                        <div className="form-group">
                            <div className="row">
                                <div className="col-xs-4">
                                    <a className="btn btn-default form-control" onClick={(e) => this.addLanguage(formApi.values)}>Add Statement Text</a>
                                </div>
                            </div>
                        </div>

                        {formApi.values.questionType !== 'text' && formApi.values.questionType !== 'freeText' && <div>
                            <h3>Options</h3>
                            <table className="table table-striped">
                                <thead>
                                    <tr>
                                        <td>Option</td>
                                        <td>Text</td>
                                        <td>Value</td>
                                    </tr>
                                </thead>
                                <tbody>
                                    {
                                        formApi.values.questionOptions.sort((qo1: QuestionOption, qo2: QuestionOption) => {
                                            if (qo1.sort > qo2.sort) {
                                                return 1;
                                            } else if (qo1.sort < qo2.sort) {
                                                return -1;
                                            }
                                            return 0;
                                        }).map((qo: QuestionOption, ind: number) => {
                                            return <tr key={`qo-${ind}`}>
                                                <td>
                                                    # {qo.sort}
                                                </td>
                                                <td>
                                                    {
                                                        qo.questionOptionTexts.map((qot: QuestionOptionText, qotInd: number) => {
                                                            return <Text className="form-control" key={`qo-${ind}-${qotInd}`} field={['questionOptions', ind, 'questionOptionTexts', qotInd, 'value']} />;
                                                        })
                                                    }
                                                </td>
                                                <td>
                                                    <Text disabled={this.props.hasPublishedVersion} className="form-control" field={['questionOptions', ind, 'value']} />
                                                </td>
                                            </tr>;
                                        })
                                    }
                                </tbody>
                            </table>

                            <br />

                            <h3>Score Weights</h3>

                            {
                                formApi.values.questionOptions.map((opt: any, ind: number) => {
                                    if (opt.scoreWeights.length === 0) {
                                        return <div key={`qo-${ind}`} />;
                                    }
                                    return <table className="table" key={`qo-${ind}`}>
                                    <tbody>
                                        <tr>
                                            <td>Score Type</td>
                                            <td>Survey Version</td>
                                            <td>Option Value</td>
                                            <td>Score Weight</td>
                                        </tr>
                                        {
                                            opt.scoreWeights.map((sw: any, swInd: number) => {
                                                return <tr key={`sw-${swInd}`}>
                                                    <td>{sw.scoreType ? sw.scoreType.name : ''}</td>
                                                    <td>{sw.surveyVersionID}</td>
                                                    <td>{opt.value}</td>
                                                    <td>{sw.weight}</td>
                                                </tr>;
                                            })
                                        }
                                        </tbody>
                                        </table>;
                                })
                            }
                        </div>}

                        <button type="submit" className="btn btn-primary">Save</button>
                    </form>
                )}
            </Form>
        );
    }

    private removeLanguage(formValues: any, ind: number) {
        formValues.questionTexts.splice(ind, 1);
        this.forceUpdate();
    }

    private addLanguage(formValues: any) {
        var qt = new QuestionText();
        qt.languageId = this.state.addLanguageId;
        formValues.questionTexts.push(qt);
        this.forceUpdate();
    }
}


// WEBPACK FOOTER //
// ./src/components/question/QuestionForm.tsx